import React, { useEffect, useState } from 'react';
import './ParcelTapDebug.css';

/** True when the URL carries ?parcel_debug=1, matching BaseMap's console flag. */
export function isParcelDebugEnabled(search) {
  try {
    const qs = search !== undefined ? search : (typeof window !== 'undefined' ? window.location.search : '');
    return new URLSearchParams(qs || '').get('parcel_debug') === '1';
  } catch (_) {
    return false;
  }
}

/** Which map shell state the app is in, since each positions widgets differently. */
export function shellStateLabel(doc) {
  const d = doc || document;
  try {
    const shell = d.querySelector('.min-vh-100');
    const cls = (shell && shell.className) || '';
    const parts = [];
    if (cls.includes('app-shell--map-maximized')) parts.push('max');
    if (cls.includes('app-shell--map-framed')) parts.push('framed');
    if (cls.includes('app-shell--map-fullbleed')) parts.push('bleed');
    if (d.querySelector('.my-ground-workspace--board')) parts.push('board');
    return parts.length ? parts.join('+') : 'none';
  } catch (_) {
    return 'none';
  }
}

/**
 * Where the Identify control sits relative to the viewport and the fixed tab
 * bar. The widget stack is positioned against the map frame while the tab bar
 * is fixed to the viewport, so the two can collide in states that are not
 * obvious from the CSS. Measure the collision rather than infer it.
 */
export function measureIdentifyControl(doc) {
  const d = doc || document;
  try {
    const el = d.querySelector('.parcel-identify-control');
    if (!el || !el.getBoundingClientRect) return null;
    const rect = el.getBoundingClientRect();
    // A display:none control still answers querySelector but has no box, which
    // is a different bug from one positioned off-screen.
    if (!rect.width && !rect.height) return { hidden: true };
    const nav = d.querySelector('.mobile-nav-bar');
    const navRect = nav && nav.getBoundingClientRect ? nav.getBoundingClientRect() : null;
    const mapEl = d.querySelector('.map-container');
    const mapRect = mapEl && mapEl.getBoundingClientRect ? mapEl.getBoundingClientRect() : null;
    const viewportH = (typeof window !== 'undefined' && window.visualViewport)
      ? window.visualViewport.height
      : (typeof window !== 'undefined' ? window.innerHeight : 0);
    return {
      hidden: false,
      // Negative means the control has fallen below the visible viewport.
      aboveFold: Math.round(viewportH - rect.bottom),
      // Positive means it overlaps the fixed tab bar by that many pixels.
      behindNav: navRect ? Math.round(rect.bottom - navRect.top) : null,
      // Positive means the map container itself extends below the fold, which
      // drags everything anchored to its bottom edge out of sight with it.
      mapOverhang: mapRect ? Math.round(mapRect.bottom - viewportH) : null
    };
  } catch (_) {
    return null;
  }
}

/**
 * On-screen readout of parcel Identify state. A phone has no console, and a tap
 * that fires but hits nothing looks identical to a tap that never fired, so the
 * only way to tell them apart on a real device is to show the numbers.
 */
export function ParcelTapDebug({
  show,
  mapRef,
  isPhone,
  parcelsVisible,
  identifyOn,
  lotSelectionOn,
  drawingMode
}) {
  const [tap, setTap] = useState(null);
  const [geometry, setGeometry] = useState(null);

  useEffect(() => {
    if (!show) return undefined;
    const read = () => {
      try {
        setTap((mapRef && mapRef.current && mapRef.current.__yona_parcelTapDebug) || null);
        setGeometry(measureIdentifyControl());
      } catch (_) {}
    };
    read();
    const timer = setInterval(read, 400);
    return () => clearInterval(timer);
  }, [show, mapRef]);

  if (!show) return null;

  const flag = (value) => (value ? 'yes' : 'no');
  const width = typeof window !== 'undefined' ? window.innerWidth : 0;
  const coarse = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false;

  return (
    <div className="parcel-tap-debug" data-testid="parcel-tap-debug">
      <div className="parcel-tap-debug__row">width {width} · coarse {flag(coarse)} · phone {flag(isPhone)}</div>
      <div className="parcel-tap-debug__row">parcels {flag(parcelsVisible)} · identify {flag(identifyOn)}</div>
      {/* The four inputs to canOfferParcelIdentify, so a false gate names itself. */}
      <div className="parcel-tap-debug__row">lots {flag(lotSelectionOn)} · draw {drawingMode || 'select'}</div>
      <div className="parcel-tap-debug__row">shell {shellStateLabel()}</div>
      <div className="parcel-tap-debug__row">
        {!geometry && 'btn not rendered'}
        {geometry && geometry.hidden && 'btn display:none'}
        {geometry && !geometry.hidden
          && `btn fold ${geometry.aboveFold} · nav ${geometry.behindNav === null ? 'none' : geometry.behindNav} · map over ${geometry.mapOverhang === null ? '?' : geometry.mapOverhang}`}
      </div>
      {tap ? (
        <>
          <div className="parcel-tap-debug__row">
            tap {tap.point ? `${tap.point.x},${tap.point.y}` : '—'} · zoom {tap.zoom ?? '—'} · {tap.at}
          </div>
          <div className="parcel-tap-debug__row">
            hit {flag(tap.hit)}{tap.parcelId ? ` (${tap.parcelId})` : ''} · layers {tap.layers && tap.layers.length ? tap.layers.length : 0}
          </div>
        </>
      ) : (
        <div className="parcel-tap-debug__row">no identify tap recorded yet</div>
      )}
    </div>
  );
}

export default ParcelTapDebug;
